import React from "react";
import styled from "styled-components";
import {
  parseMsSsim,
  classifyVerdict,
  Verdict,
  RegressionThresholds,
} from "../utils/regressionEvidence";

const PanelContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-top: 8px;
`;

const MetricCard = styled.div<{ highlight?: boolean }>`
  background: ${(p) => (p.highlight ? "#e3f2fd" : "#fff")};
  border: 1px solid ${(p) => (p.highlight ? "#1976d2" : "#e0e0e0")};
  border-radius: 4px;
  padding: 6px 10px;
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #212121;
`;

const MetricLabel = styled.div`
  font-size: 10px;
  color: #757575;
  margin-top: 2px;
`;

const VerdictBadge = styled.span<{ verdict: Verdict }>`
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: ${(p) =>
    p.verdict === "ok"
      ? "#4caf50"
      : p.verdict === "review"
      ? "#ff9800"
      : p.verdict === "ng"
      ? "#f44336"
      : "#9e9e9e"};
  border-radius: 10px;
`;

const NoScoreMessage = styled.div`
  padding: 12px;
  color: #757575;
  font-size: 12px;
  text-align: center;
  background: #f5f5f5;
  border-radius: 4px;
  margin-top: 8px;
`;

export interface MetricsPanelProps {
  scoreReason: string | undefined;
  thresholds?: RegressionThresholds;
}

const verdictLabel = (v: Verdict): string =>
  v === "ok" ? "OK" : v === "review" ? "AI判定" : v === "ng" ? "NG" : "不明";

const fmt = (n: number | undefined): string =>
  n === undefined ? "—" : n.toFixed(4);

export const MetricsPanel: React.FC<MetricsPanelProps> = ({
  scoreReason,
  thresholds,
}) => {
  const { full, target, min } = parseMsSsim(scoreReason);
  if (full === undefined && target === undefined && min === undefined) {
    return (
      <NoScoreMessage title={scoreReason ?? ""}>スコア情報なし</NoScoreMessage>
    );
  }
  const verdict = classifyVerdict(min, thresholds);
  return (
    <PanelContainer>
      {target !== undefined ? (
        <>
          <MetricCard>
            <MetricValue>{fmt(full)}</MetricValue>
            <MetricLabel>full SSIM</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue>{fmt(target)}</MetricValue>
            <MetricLabel>target SSIM</MetricLabel>
          </MetricCard>
          <MetricCard highlight>
            <MetricValue>{fmt(min)}</MetricValue>
            <MetricLabel>min SSIM（判定値）</MetricLabel>
          </MetricCard>
        </>
      ) : (
        <MetricCard highlight>
          <MetricValue>{fmt(full)}</MetricValue>
          <MetricLabel>SSIM（判定値）</MetricLabel>
        </MetricCard>
      )}
      <MetricCard>
        <VerdictBadge verdict={verdict}>{verdictLabel(verdict)}</VerdictBadge>
        <MetricLabel>判定</MetricLabel>
      </MetricCard>
    </PanelContainer>
  );
};
